<template>
  <div class="data-record">
    <div v-if="!isMobileView" class="team row row--hover box box--default">
      <div class="row__data row__data--small row__data--image team__logo">
        <img v-if="userThumbnail" :src="userThumbnail" alt="" />
        <div v-else class="thumbnail thumbnail--team" :style="color">
          {{ parseInitials(team.name) }}
        </div>
      </div>
      <div class="row__data row__data--large row__data--name">
        {{ team.name }}
      </div>

      <div class="row__data row__data--medium">
        {{ team.finma_nr || '-' }}
      </div>

      <div class="row__data row__data--medium">
        {{ team.domain || '-' }}
      </div>

      <div class="row__data row__data--large row__data--address">
        {{ fullAddress }}
      </div>

      <div class="row__data row__data--large team__mandates" style="justify-content: flex-start;">
        <div v-if="team.mandate_types && team.mandate_types.length > 0" class="mandates-list">
          <div v-for="mandateType in team.mandate_types" :key="mandateType.id" class="mandate-item">
            {{ mandateType.name }}
          </div>
        </div>
        <span v-else>-</span>
      </div>

      <div class="row__data row__data--action row__data--small" @click.self="showActionsDropdown = !showActionsDropdown"
        v-if="hasActions">
        <TeamsRowActionDropdown v-if="showActionsDropdown" @toggleDropdown="showActionsDropdown = $event"
          :onEdit="canEdit ? editTeam : null" :onDelete="canDelete ? removeTeam : null" :onTaxes="canAccessTaxes ? taxes : null" />
      </div>
    </div>

    <article v-if="isMobileView" class="data-card box box--default">
      <header class="data-card__header">
        <button type="button" class="data-card__identity">
          <span class="data-card__avatar">
            <img v-if="userThumbnail" :src="userThumbnail" :alt="team.name" />
            <span v-else class="thumbnail thumbnail--team" :style="color">
              {{ parseInitials(team.name) }}
            </span>
          </span>
          <span class="data-card__name">{{ team.name }}</span>
        </button>

        <div class="data-card__action row__data--action" role="button" tabindex="0"
          :aria-label="`${$t('buttons.editBtn')} / ${$t('buttons.deleteBtn')}`"
          @click.self="showCardActionsDropdown = !showCardActionsDropdown"
          @keydown.enter.self="showCardActionsDropdown = !showCardActionsDropdown"
          @keydown.space.prevent.self="showCardActionsDropdown = !showCardActionsDropdown"
          v-if="hasActions">
          <TeamsRowActionDropdown v-if="showCardActionsDropdown" @toggleDropdown="showCardActionsDropdown = $event"
            :onEdit="canEdit ? editTeam : null" :onDelete="canDelete ? removeTeam : null" :onTaxes="canAccessTaxes ? taxes : null" />
        </div>
      </header>

      <div v-if="team && team.domain" class="data-card__contacts">
        <a class="data-card__contact" :href="`https://${team.domain}`">
          <span class=" company__icon--web"></span>
          <span>{{ team.domain }}</span>
        </a>
      </div>

      <dl v-if="team" class="data-card__details">
        <div v-if="team.finma_nr" class="data-card__detail">
          <dt>FINMA Nr.</dt>
          <dd>{{ team.finma_nr }}</dd>
        </div>
        <div v-if="team.street || team.number" class="data-card__detail">
          <dt>{{ $t(`teams.${"tableHeader"}.${"address"}`) }}</dt>
          <dd><span v-if="team.street">{{ team.street }}</span> <span v-if="team.number">{{ team.number }}</span></dd>
        </div>
        <div v-if="team.mandate_types && team.mandate_types.length > 0"
          class="data-card__detail-full data-card__detail">
          <dt>{{ $t(`teams.${"tableHeader"}.${"mandateTypes"}`) }}</dt>
          <dd v-for="mandateType in team.mandate_types" :key="mandateType.id" class="dd-mandate-type">
            {{ mandateType.name }}
          </dd>
        </div>
      </dl>
    </article>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import TeamsRowActionDropdown from "@/components/TeamsActionDropdown";
import { parseInitials } from "@/helpers/entityParsers";

defineOptions({
  name: "Team"
});

const props = defineProps({
  team: {
    required: true,
    type: Object,
  },
  deleteTeam: {
    required: true,
    type: Function,
  },
});

const store = useStore();
const router = useRouter();

// State
const showActionsDropdown = ref(false);
const showCardActionsDropdown = ref(false);
const isMobileView = ref(false);

const updateMobileView = () => {
  isMobileView.value = window.innerWidth <= 900;
};

onMounted(() => {
  updateMobileView();
  window.addEventListener("resize", updateMobileView);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateMobileView);
});
// Computed
const userThumbnail = computed(() =>
  props.team.photo ? props.team.photo.thumbnail : null
);

const color = computed(() =>
  props.team.color
    ? { background: props.team.color }
    : { background: "#7f8fa4" }
);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const canEdit = computed(() => {
  return isSuperAdmin.value;
});
const canDelete = computed(() => {
  return isSuperAdmin.value;
});
const canAccessTaxes = computed(() => {
  return isSuperAdmin.value;
});
const hasActions = computed(() => isSuperAdmin.value);

const fullAddress = computed(() => {
  const parts = [];
  if (props.team.street) parts.push(props.team.street);
  if (props.team.number) parts.push(props.team.number);
  const streetAddress = parts.join(' ');

  const cityParts = [];
  if (props.team.zip) cityParts.push(props.team.zip);
  if (props.team.city) cityParts.push(props.team.city);
  const cityAddress = cityParts.join(' ');

  const fullParts = [];
  if (streetAddress) fullParts.push(streetAddress);
  if (cityAddress) fullParts.push(cityAddress);

  return fullParts.length > 0 ? fullParts.join(', ') : '-';
});

// Methods
const editTeam = () => {
  store.commit("setTeamToEdit", props.team);
  store.commit("setTeamIsEdit", true);
  store.commit("setShowAddTeamModal", true);
};

const removeTeam = () => {
  props.deleteTeam({
    display: true,
    message: `'${props.team.name}'`,
    action: store._actions.deleteTeam,
    id: props.team.id,
  });
};

const taxes = () => {
  router.push({
    name: "TeamTaxes",
    params: {
      id: props.team.id,
    },
  });
};
</script>

<style scoped lang="scss">
.row__data--action {
  max-width: 70px;
}

.team__logo {
  max-width: 70px;
}

.team__mandates {
  align-items: flex-start !important;
  text-align: left !important;

  .mandates-list {
    max-height: 60px;
    overflow-y: auto;
    width: 100%;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--color-9, #ccc);
      border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: var(--color-8, #999);
    }
  }

  .mandate-item {
    padding: 2px 0;
    line-height: 1.4;
    font-size: 13px;
    text-align: left;
  }
}

.company__icon--web {
  height: 24px;
  width: 24px; 
  background: url("../../assets/icons/default/web_icon.svg"); 
}

.dd-mandate-type {
  margin-bottom: 5px !important;

  &:last-child {
    margin-bottom: 0 !important;
  }
}
</style>
